<!-- TOP BAR -->
<header class="topbar">
  <a class="brand" (click)="navigateToRoute('/docs/lca-api/auth')">
    <span class="brand-mark">
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="9" cy="12" r="6" fill="white" fill-opacity="0.95" />
        <circle cx="15" cy="12" r="6" fill="white" fill-opacity="0.55" />
      </svg>
    </span>
    <span class="brand-name">PIQET</span>
    <span class="brand-tag">Docs</span>
  </a>
  <span class="version-select">
    <span class="dot"></span>
    v1.0
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.5"
    >
      <path d="M6 9l6 6 6-6" />
    </svg>
  </span>
  <div class="topbar-right">
    <a class="nav-link" (click)="navigateToRoute('/docs/lca-api')">LCA API</a>
    <a class="nav-link" (click)="navigateToRoute('/docs/lca-api/auth')">Auth API</a>
    <a class="nav-link" (click)="navigateToRoute('/docs/quick-reference')">Quick Reference</a>
  </div>
</header>

<div class="layout">
  <!-- SIDEBAR -->
  <aside class="sidebar">
    <div class="search">
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <input
        piqetCtrlKFocus
        type="text"
        placeholder="Search reference..."
        [(ngModel)]="searchQuery"
      />
      <kbd>Ctrl+K</kbd>
    </div>

    <nav>
      <ng-container *ngFor="let section of filteredNavSections">
        <div class="nav-section">
          <div class="nav-section-title">{{ section.title }}</div>

          <!-- Simple children (no collapsible parent) -->
          <ng-container *ngIf="section.children">
            <div
              class="nav-child"
              *ngFor="let child of section.children"
              [class.active]="child.isActive"
              (click)="setActiveNavChild(child)"
            >
              <span *ngIf="child.method" class="method" [ngClass]="child.methodClass">{{
                child.method
              }}</span>
              {{ child.label }}
            </div>
          </ng-container>

          <!-- Collapsible items -->
          <ng-container *ngIf="section.items">
            <ng-container *ngFor="let item of section.items">
              <div class="nav-item" [class.open]="item.isExpanded" (click)="toggleNavItem(item)">
                <svg
                  class="chev"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="m9 18 6-6-6-6" />
                </svg>
                {{ item.label }}
              </div>
              <div class="nav-children" [style.display]="item.isExpanded ? 'block' : 'none'">
                <div
                  class="nav-child"
                  *ngFor="let child of item.children"
                  [class.active]="child.isActive"
                  (click)="setActiveNavChild(child); $event.stopPropagation()"
                >
                  <span *ngIf="child.method" class="method" [ngClass]="child.methodClass">{{
                    child.method
                  }}</span>
                  {{ child.label }}
                </div>
              </div>
            </ng-container>
          </ng-container>
        </div>
      </ng-container>
    </nav>
  </aside>

  <!-- MAIN CONTENT -->
  <main class="content">
    <nav class="breadcrumb fade-in fade-in-1">
      API Reference
      <span class="sep">/</span>
      Authentication
      <span class="sep">/</span>
      <span class="current">Overview</span>
    </nav>

    <div class="title-row fade-in fade-in-1" id="overview">
      <h1 class="page-title">Bearer JWT <em>authentication.</em></h1>
      <span class="version-badge-large">v1.0</span>
    </div>

    <p class="lede fade-in fade-in-2">
      The PIQET LCA Calculation API uses JWT (JSON Web Token) based authentication. All protected
      endpoints require a valid Bearer token in the
      <code class="inline">Authorization</code> header. Access tokens are short-lived by design —
      pair them with the refresh flow for long-running integrations.
    </p>

    <!-- Endpoint URL bar -->
    <div class="endpoint fade-in fade-in-2" id="endpoint-url">
      <span class="method method-post">POST</span>
      <span class="path">{{ apiBaseUrl }}</span>
      <button
        (click)="copyUrl(apiBaseUrl, $event)"
        [title]="isCopied(apiBaseUrl) ? 'Copied!' : 'Copy endpoint'"
      >
        <svg
          *ngIf="!isCopied(apiBaseUrl)"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
        <svg
          *ngIf="isCopied(apiBaseUrl)"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        {{ isCopied(apiBaseUrl) ? 'Copied' : 'Copy' }}
      </button>
    </div>

    <!-- Security scheme -->
    <div class="security-box fade-in fade-in-2" id="token-structure">
      <div class="lock-ico">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      </div>
      <div>
        <div class="security-title">Security Scheme &middot; bearerAuth</div>
        <p class="security-text">
          Supply the access token in the <code>Authorization</code> header using the Bearer scheme —
          for example: <code>Authorization: Bearer eyJhbGciOi...</code>. Tokens are signed HS256
          JWTs with a 60-minute lifetime.
        </p>
      </div>
    </div>

    <!-- Authentication Flow -->
    <div class="flow fade-in fade-in-3">
      <div class="flow-header">
        <span class="flow-label">Authentication flow</span>
        <span class="flow-meta">OAuth 2.0 &middot; Resource Owner Password Credentials</span>
      </div>
      <div class="flow-steps">
        <ng-container *ngFor="let step of authFlowSteps; let last = last">
          <div class="flow-step">
            <div class="flow-step-num">{{ step.number }}</div>
            <div class="flow-step-title">{{ step.title }}</div>
            <div class="flow-step-meta">{{ step.meta }}</div>
          </div>
          <div class="flow-arrow" *ngIf="!last">
            <svg width="20" height="14" viewBox="0 0 20 14" fill="none">
              <path
                d="M0 7h18m0 0-5-5m5 5-5 5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>
        </ng-container>
      </div>
    </div>

    <!-- API ENDPOINTS -->
    <h2 class="section" id="endpoints">API endpoints</h2>
    <p class="section-sub">
      Three endpoints govern the token lifecycle: obtain, renew, and revoke. All accept and return
      <code class="inline">application/json</code>.
    </p>

    <div
      class="endpoint-card"
      *ngFor="let ep of endpoints"
      [class.open]="ep.isExpanded"
      [id]="ep.id"
    >
      <div class="endpoint-head" (click)="toggleEndpoint(ep)">
        <span class="expand-ico">{{ ep.isExpanded ? '−' : '+' }}</span>
        <span class="method method-post">{{ ep.method }}</span>
        <span class="endpoint-name">{{ ep.name }}</span>
        <span class="endpoint-path">{{ ep.url }}</span>
      </div>
      <div class="endpoint-body" *ngIf="ep.isExpanded">
        <p class="endpoint-desc">{{ ep.description }}</p>

        <!-- Request Body -->
        <div class="fields-label">Request body</div>
        <div class="ftable">
          <div class="ftable-head">
            <span>Property</span><span>Type</span><span>Required</span><span>Description</span>
          </div>
          <div class="ftable-row" *ngFor="let prop of ep.requestBody">
            <span class="param-name">{{ prop.name }}</span>
            <span
              ><span class="type-badge" [ngClass]="getTypeClass(prop.type)">{{
                prop.type
              }}</span></span
            >
            <span
              ><span class="req-badge" [ngClass]="prop.required ? 'req-yes' : 'req-no'">{{
                prop.required ? 'Required' : 'Optional'
              }}</span></span
            >
            <span class="col-desc">{{ prop.description }}</span>
          </div>
        </div>

        <!-- Response Body -->
        <ng-container *ngIf="ep.responseBody.length > 0">
          <div class="fields-label">
            <span
              class="status-badge"
              [ngClass]="ep.responseStatus === '200 OK' ? 'status-200' : 'status-204'"
              >{{ ep.responseStatus }}</span
            >
            Response body
          </div>
          <div class="ftable">
            <div class="ftable-head">
              <span>Property</span><span>Type</span><span>Nullable</span><span>Description</span>
            </div>
            <div class="ftable-row" *ngFor="let prop of ep.responseBody">
              <span class="param-name">{{ prop.name }}</span>
              <span
                ><span class="type-badge" [ngClass]="getTypeClass(prop.type)">{{
                  prop.type
                }}</span></span
              >
              <span
                ><span class="req-badge req-no">{{ prop.nullable ? 'Yes' : 'No' }}</span></span
              >
              <span class="col-desc">{{ prop.description }}</span>
            </div>
          </div>
        </ng-container>

        <!-- No-content response -->
        <ng-container *ngIf="ep.responseBody.length === 0">
          <div class="fields-label">
            <span class="status-badge status-204">{{ ep.responseStatus }}</span>
            Response
          </div>
          <p class="endpoint-desc" style="margin-top: 4px">
            Successful revocation returns an empty body with status <code class="inline">204</code>.
          </p>
        </ng-container>

        <!-- Notes -->
        <div
          class="note"
          *ngFor="let note of ep.notes"
          [ngClass]="note.type === 'info' ? 'info' : ''"
        >
          <!-- Warning icon -->
          <svg
            *ngIf="note.type === 'warning'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path
              d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
            />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <!-- Info icon -->
          <svg
            *ngIf="note.type === 'info'"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <div>
            <strong>{{ note.title }}</strong> {{ note.content }}
          </div>
        </div>
      </div>
    </div>

    <!-- Security callout -->
    <div class="callout">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 16v-4M12 8h.01" />
      </svg>
      <div>
        <strong>Store tokens safely.</strong> Treat access and refresh tokens as credentials. Never
        embed them in client-side code served to browsers, commit them to source control, or log
        them. For browser-based integrations use a backend-for-frontend pattern.
      </div>
    </div>
  </main>

  <!-- RIGHT CODE PANEL -->
  <aside class="code-panel">
    <!-- Request card -->
    <div class="code-card fade-in fade-in-2">
      <div class="code-tabs">
        <div class="code-tab-set">
          <button
            *ngFor="let ex of codeExamples"
            class="code-tab"
            [class.active]="activeCodeLang === ex.lang"
            (click)="setCodeLang(ex.lang)"
          >
            {{ ex.label }}
          </button>
        </div>
        <button class="code-copy" [class.copied]="reqCopied" (click)="copyCode('request')">
          <svg
            *ngIf="!reqCopied"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <svg
            *ngIf="reqCopied"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          {{ reqCopied ? 'Copied' : 'Copy' }}
        </button>
      </div>
      <ng-container *ngFor="let ex of codeExamples">
        <pre class="code-body code-pane" [class.active]="activeCodeLang === ex.lang">{{
          ex.code
        }}</pre>
      </ng-container>
    </div>

    <!-- Response card -->
    <div class="code-card fade-in fade-in-3">
      <div class="code-tabs">
        <div class="code-tab-set">
          <button
            *ngFor="let resp of responseExamples"
            class="code-tab"
            [class.active]="activeRespStatus === resp.status"
            (click)="setRespStatus(resp.status)"
          >
            <span class="resp-status" [ngClass]="resp.statusClass">&bull; {{ resp.label }}</span>
          </button>
        </div>
        <button class="code-copy" [class.copied]="respCopied" (click)="copyCode('response')">
          <svg
            *ngIf="!respCopied"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
          <svg
            *ngIf="respCopied"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
          {{ respCopied ? 'Copied' : 'Copy' }}
        </button>
      </div>
      <ng-container *ngFor="let resp of responseExamples">
        <pre class="code-body code-pane" [class.active]="activeRespStatus === resp.status">{{
          resp.code
        }}</pre>
      </ng-container>
    </div>
  </aside>

  <!-- FOOTER -->
  <footer>
    <div>PIQET &middot; <span class="footer-version">v1.0</span></div>
    <div class="databases">
      <span>OAuth 2.0</span>
      <span class="sep">&middot;</span>
      <span>JWT HS256</span>
      <span class="sep">&middot;</span>
      <span>OpenAPI 3.1</span>
    </div>
    <div class="status-indicator">
      <span class="dot"></span>
      Auth service operational
    </div>
  </footer>
</div>
